import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import { Component } from '../core/smpl-components.js'
import { StackFade } from '../modules/embla-stack-fade.js'
import { MqlMatchMedia } from '../utils/mql-match-media.js'

// Crossfade where there is a mouse, swipe where there is a finger.
// Keep in sync with the same query in _templates/_studio.scss.
const FADE_AT = '(hover: hover) and (pointer: fine)'

/**
 * FadeSlider
 *
 * One Embla instance in two guises: where `fadeAt` matches the slides are stacked
 * and crossfaded (StackFade), everywhere else it stays a plain carousel so it can
 * be swiped. StackFade has no breakpoint handling of its own, so it gets added and
 * removed by re-initializing the carousel — which keeps the options and the
 * selected slide.
 *
 * The plugin only stacks; the opacity lives in CSS, driven by `is-active`.
 *
 * Move to modules/ once a second template needs it.
 */
class FadeSlider {
	constructor(element, options = {}) {
		this.options = {
			fadeAt: FADE_AT,
			viewportSelector: '.fade-slider__viewport',
			slideSelector: '.fade-slider__slide',
			activeClass: 'is-active',
			initializedClass: 'is-initialized',
			// Stacked slides can't be dragged, so a click is the only way forward.
			// Turn off once the slider has real controls.
			clickToAdvance: true,
			autoplay: true,
			autoplayOptions: {
				delay: 3000,
				// A swipe or a click resets the timer instead of ending autoplay for
				// good. Set stopOnMouseEnter for pause-on-hover.
				stopOnInteraction: false,
			},
			emblaOptions: {},
			...options,
		}

		// Slides that advance on their own are disorienting for anyone who asked for
		// less motion.
		this.autoplayEnabled = this.options.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches

		this.element = element
		this.elViewport = this.element.querySelector(this.options.viewportSelector)
		this.elSlides = Array.from(this.element.querySelectorAll(this.options.slideSelector))
		this.embla = null

		if (!this.elViewport || this.elSlides.length < 2) return

		this.init()
	}

	init() {
		this.embla = EmblaCarousel(
			this.elViewport,
			{
				loop: true,
				align: 'start',
				...this.options.emblaOptions,
			},
			this.buildPlugins(false),
		)

		this.initFade()

		this.bndOnSelect = this.onSelect.bind(this)
		this.embla.on('select', this.bndOnSelect)

		if (this.options.clickToAdvance) {
			this.bndOnClick = this.onClick.bind(this)
			this.elViewport.addEventListener('click', this.bndOnClick)
		}

		this.onSelect()
		this.element.classList.add(this.options.initializedClass)
	}

	initFade() {
		this.fadeActive = false

		this.mqlFade = new MqlMatchMedia(this.options.fadeAt, (matches) => {
			if (matches === this.fadeActive) return

			this.fadeActive = matches
			// The stacked slides don't move, so a drag would have no visual feedback.
			this.embla.reInit({ watchDrag: !matches }, this.buildPlugins(matches))
		})
	}

	// reInit replaces the plugin list, so autoplay has to be handed back in every
	// time. It gets a fresh instance and so a fresh timer — a mode switch is rare
	// enough that restarting the countdown doesn't show.
	buildPlugins(fadeActive) {
		const plugins = []

		if (fadeActive) {
			plugins.push(StackFade())
		}
		if (this.autoplayEnabled) {
			plugins.push(Autoplay(this.options.autoplayOptions))
		}

		return plugins
	}

	getInstance() {
		return this.embla
	}

	getAutoplay() {
		return this.embla ? this.embla.plugins().autoplay : null
	}

	onSelect() {
		const index = this.embla.selectedScrollSnap()

		// Only the selected slide is marked; the outgoing one fades out on its own.
		// The slides differ in height, so it can't be parked opaque underneath — it
		// would stick out past the incoming one. See StackFade + _studio.scss.
		this.elSlides.forEach((el, i) => {
			el.classList.toggle(this.options.activeClass, i === index)
		})
	}

	onClick() {
		if (!this.fadeActive) return

		this.embla.scrollNext()

		// Dragging is off in fade mode, so Embla reports no interaction and autoplay
		// never notices the click — it would advance again right after. Reset by hand.
		const autoplay = this.getAutoplay()
		if (autoplay) {
			autoplay.reset()
		}
	}

	destroy() {
		if (!this.embla) return

		if (this.mqlFade) {
			this.mqlFade.destroy()
			this.mqlFade = null
		}
		if (this.bndOnClick) {
			this.elViewport.removeEventListener('click', this.bndOnClick)
		}

		this.embla.off('select', this.bndOnSelect)
		this.embla.destroy()
		this.embla = null

		this.element.classList.remove(this.options.initializedClass)
	}
}

// -------------------------------------------------------------------------- //

/**
 * StudioSlider
 *
 * The image slider on the studio page. Selector: `.js-studio-slider`.
 */
export default class extends Component {
	prepare() {
		this.slider = new FadeSlider(this.element)
	}

	mount() {}

	unmount() {
		if (this.slider) {
			this.slider.destroy()
			this.slider = null
		}
	}
}
